/**
 * Account Modal
 *
 * Create/Edit account with type selector (Firm vs Individual).
 * Common: name, description, accountOwner. Individual: phone, email.
 */

import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useCreateAccount, useUpdateAccount } from '../../services/queries/accountQueries';
import { useToast } from '../../contexts/ToastContext';
import { X } from 'lucide-react';
import type { Account, AccountType } from '../../types/index';

interface AccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  account?: Account | null;
  onSuccess?: () => void;
}

export const AccountModal: React.FC<AccountModalProps> = ({
  isOpen,
  onClose,
  account,
  onSuccess
}) => {
  const { success, error: showError } = useToast();
  const isEditMode = !!account;

  const createAccount = useCreateAccount();
  const updateAccount = useUpdateAccount();

  const [formData, setFormData] = useState({
    name: '',
    type: 'firm' as AccountType,
    description: '',
    accountOwner: '',
    phone: '',
    email: '',
    billingAddress: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (account) {
      setFormData({
        name: account.name,
        type: account.type,
        description: account.description || '',
        accountOwner: account.accountOwner || '',
        phone: account.phone || '',
        email: account.email || '',
        billingAddress: account.billingAddress || ''
      });
    } else {
      setFormData({
        name: '',
        type: 'firm',
        description: '',
        accountOwner: '',
        phone: '',
        email: '',
        billingAddress: ''
      });
    }
    setErrors({});
  }, [account, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Account name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      showError('Validation Error', 'Please fix the errors before submitting');
      return;
    }

    const payload: Partial<Account> = {
      name: formData.name.trim(),
      type: formData.type,
      description: formData.description.trim() || undefined,
      accountOwner: formData.accountOwner.trim() || undefined,
    };
    if (formData.type === 'individual') {
      payload.phone = formData.phone.trim() || undefined;
      payload.email = formData.email.trim() || undefined;
      payload.billingAddress = formData.billingAddress.trim() || undefined;
    }

    if (isEditMode && account) {
      updateAccount.mutate(
        { id: account.id, updates: payload },
        {
          onSuccess: () => {
            success('Account updated', `${payload.name} has been updated`);
            onClose();
            onSuccess?.();
          },
          onError: () => {
            showError('Error', 'Failed to update account');
          }
        }
      );
    } else {
      createAccount.mutate(payload as Omit<Account, 'id'>, {
        onSuccess: (data) => {
          success('Account created', `${data.name} has been created`);
          onClose();
          onSuccess?.();
        },
        onError: () => {
          showError('Error', 'Failed to create account');
        }
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white dark:bg-[#1e293b] rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {isEditMode ? 'Edit Account' : 'New Account'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Account Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white ${
                    errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  disabled={isEditMode}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white disabled:opacity-70"
                >
                  <option value="firm">Firm</option>
                  <option value="individual">Individual</option>
                </select>
                {isEditMode && (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Type cannot be changed after creation</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Account Owner
                </label>
                <input
                  type="text"
                  name="accountOwner"
                  value={formData.accountOwner}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white"
                />
              </div>

              {formData.type === 'individual' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Phone
                    </label>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Billing Address
                    </label>
                    <input
                      type="text"
                      name="billingAddress"
                      value={formData.billingAddress}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#334155] border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-[#475569] transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createAccount.isPending || updateAccount.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {createAccount.isPending || updateAccount.isPending
                  ? 'Saving...'
                  : isEditMode
                  ? 'Update Account'
                  : 'Create Account'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
